import React from "react";
import { Chart } from "chart.js";
import { ChartJsContainer } from "./ChartJsContainer";
import ChartDataLabels from "chartjs-plugin-datalabels";
import _ from "lodash";
import styles from "./SolidDashLineChart.module.scss";
import { isUnitDollar, prefixValue, suffixValue, Unit } from "../../Components/processors";
import { formatNumber } from "../../utils";

// console.log(ChartDataLabels);
Chart.plugins.register(ChartDataLabels);
const WHITE_GREY = "#f5f5f5";
const LARGE_PROVIDER_BACKGROUND = `rgba(96, 96, 96, 0.7)`;

// let XScale = Chart.Scale.extend({
//     /* extensions ... */
// });

// catHalfWidth >= halfAnnotationLength
const shortHalfAnnotationLength = 65;
const longHalfAnnotationLength = 80;
const offsetUnrealGap = 25;

interface SolidDashLineChartProps {
    measure: string;

    labels: (string | number)[];
    solidValues: number[];
    solidColor: string;
    solidLabel: string;

    dashValues: number[];
    dashLabel: string;
    dashColor?: string;
    dashAnnotation: string;
    unit: Unit;

    yAxisLabelFormatter?: (value: number) => string;
}
export const SolidDashLineChart: React.FC<SolidDashLineChartProps> = ({
    measure,
    labels,
    solidColor,
    solidValues,
    solidLabel,
    unit,
    dashLabel,
    dashValues,
    dashColor,
    dashAnnotation,
    yAxisLabelFormatter
}) => {
    const hasDashLine = dashValues?.find(d => d != null) != null;

    const max = _.max([...(solidValues ?? []), ...(dashValues ?? [])]);
    // const min = _.min([_.min([...(solidValues ?? []), ...(dashValues ?? [])]) * 1.2, 0]);
    const min = _.min([...(solidValues ?? []), ...(dashValues ?? [])]);

    const h = 300;
    const t = 50;
    const b = 50;
    const scale = (max - min) / (h - t - b);
    const vtop = max + scale * t;
    const vbottom = isUnitDollar(unit) ? Math.max(min - scale * b, 0) : min - scale * b;

    const chart = {
        data: {
            datasets: [
                !hasDashLine
                    ? null
                    : {
                          pointRadius: 0,
                          pointHoverRadius: 0,
                          lineTension: 0,
                          fill: false,
                          spanGaps: true,
                          borderWidth: 4,
                          type: "line",
                          showLine: false,
                          borderDash: [10, 7],
                          data: dashValues.map(d => (d ?? 0) + scale * offsetUnrealGap),
                          // label: dashLabel,
                          label: `${dashLabel} fake`,
                          backgroundColor: "red",
                          borderColor: "red"
                      },
                !hasDashLine
                    ? null
                    : {
                          pointRadius: 0,
                          pointHoverRadius: 0,
                          lineTension: 0,
                          fill: false,
                          showLine: hasDashLine,
                          spanGaps: true,
                          borderWidth: 2,
                          type: "line",
                          borderDash: [10, 7],
                          data: dashValues,
                          label: `${dashLabel}`,
                          backgroundColor: dashColor ?? LARGE_PROVIDER_BACKGROUND,
                          borderColor: dashColor ?? LARGE_PROVIDER_BACKGROUND
                      },
                {
                    pointRadius: 6,
                    pointHoverRadius: 6,
                    pointBackgroundColor: "white",
                    pointHoverBackgroundColor: solidColor,
                    lineTension: 0,
                    fill: false,
                    spanGaps: true,
                    borderWidth: 4,
                    type: "line",
                    data: solidValues,
                    label: solidLabel,

                    backgroundColor: solidColor,
                    borderColor: solidColor
                }
            ].filter(d => d != null),
            labels
        },
        // options: {
        //     maintainAspectRatio: false
        // },

        options: {
            // layout: {
            //     padding: {
            //         left: 100,
            //         right: 100,
            //         top: 0,
            //         bottom: 0
            //     }
            // },
            tooltips: {
                enabled: true,
                intersect: false,
                mode: "index",
                titleFontFamily: "'Lato', sans-serif",
                bodyFontFamily: "'Lato', sans-serif",
                footerFontFamily: "'Lato', sans-serif",
                titleFontSize: 14.4,
                bodyFontSize: 14.4,
                footerFontSize: 14.4,
                callbacks: {
                    title: (d, b) => {
                        const year = _.uniq(
                            (d ?? []).map(d => d?.label).filter(d => d != null && d.trim() !== "")
                        )?.[0];
                        return `${measure ?? ""} - ${year ?? ""}`;
                    },
                    label: (context, data) => {
                        if (hasDashLine && context?.datasetIndex === 0) return "";

                        const baseLabel = data?.datasets?.[context?.datasetIndex]?.label;
                        const value = context?.value;
                        return `${baseLabel}: ${prefixValue(value, unit) ?? ""}${formatNumber(value)}${
                            suffixValue(value, unit) ?? ""
                        }`;
                    }
                }
            },
            hover: { mode: "index", intersect: false },
            legend: {
                display: false,
                onClick: false,
                labels: { fontFamily: "'Lato', sans-serif", fontSize: 13, boxWidth: 13 }
            },
            maintainAspectRatio: false,
            scales: {
                yAxes: [
                    {
                        position: "left",
                        scaleLabel: {
                            display: false,
                            labelString: ""
                        },
                        display: true,
                        stacked: false,
                        gridLines: { display: false, drawBorder: false },

                        ticks: {
                            // beginAtZero: true,
                            suggestedMax: vtop,
                            suggestedMin: vbottom, // _.min([...(solidValues ?? []), ...(dashValues ?? [])]),
                            // _.min([_.min([...(solidValues ?? []), ...(dashValues ?? [])]) * 1.2, 0]),
                            callback:
                                yAxisLabelFormatter ??
                                function (value) {
                                    return `${value}`;
                                }
                        }
                    }
                ],
                xAxes: [
                    {
                        display: true,
                        ticks: {
                            fontFamily: "'Lato', sans-serif",
                            fontSize: 12.8
                            //padding: 100
                        },
                        gridLines: { display: false, drawBorder: false }
                        // xScalePaddingRight: 100
                    }
                ]
            },
            plugins: {
                flexiTickArea: false,
                // datalabels: false,
                stacked100: false,
                doughnutlabel: false,
                labels: false,
                deferred: { yOffset: "30%", delay: 500 },
                datalabels: {
                    align: function (context) {
                        if (!hasDashLine || context?.datasetIndex !== 0) return "end";

                        return "right";

                        // const left = context?.chart?.chartArea?.left;
                        // const width = context?.chart?.width;

                        // const catNum = context?.dataset?.data?.length;

                        // if (left == null || width == null || catNum == null) return "end";

                        // const catHalfWidth = (width - left) / (catNum * 2);

                        // const halfAnnotationLength =
                        //     context?.dataset?.label?.split(" ")?.length >= 5
                        //         ? longHalfAnnotationLength
                        //         : shortHalfAnnotationLength;

                        // // console.log(catHalfWidth, halfAnnotationLength, catHalfWidth >= halfAnnotationLength);
                        // if (catHalfWidth >= halfAnnotationLength) return "end";

                        // // return "right";
                        // return "end";
                    },
                    anchor: "end",
                    clamp: true,
                    clip: false,
                    // offset: 5,
                    offset: function (context) {
                        if (!hasDashLine || context?.datasetIndex !== 0) return 0;

                        // if (context?.datasetIndex !== 0) return 0;

                        // console.log(context);
                        const left = context?.chart?.chartArea?.left;
                        const width = context?.chart?.width;

                        const catNum = context?.dataset?.data?.length;

                        if (left == null || width == null || catNum == null) return 0;

                        const catHalfWidth = (width - left) / (catNum * 2);

                        const halfAnnotationLength =
                            context?.dataset?.label?.split(" ")?.length >= 5
                                ? longHalfAnnotationLength + 10
                                : shortHalfAnnotationLength + 10;

                        // if (catHalfWidth >= halfAnnotationLength) return -offsetUnrealGap / 2;

                        // return -offsetUnrealGap / 2;
                        // return -(2 * halfAnnotationLength - catHalfWidth);
                        // return -2 * halfAnnotationLength;

                        return catHalfWidth >= halfAnnotationLength
                            ? -halfAnnotationLength
                            : -halfAnnotationLength - (halfAnnotationLength - catHalfWidth);
                        // return catHalfWidth - 2 * halfAnnotationLength;
                    },
                    // offset: 10,
                    // function (val, val2) {
                    //     // temp1.chart.chartArea.left
                    //     console.log(val, val2);

                    //     return 10;
                    // },
                    display: "auto",
                    padding: {
                        top: 5,
                        left: 10,
                        right: 10,
                        bottom: 5
                    },
                    backgroundColor: function (value) {
                        const w = value?.chart?.width;
                        const dataLength = value?.chart?.data?.datasets?.[0]?.data?.length;

                        const dataRenderIndex = w < 512 ? dataLength - 1 : dataLength - 1;
                        if (!hasDashLine) return undefined;

                        if (value?.datasetIndex !== 0 || value?.dataIndex !== dataRenderIndex) {
                            return undefined;
                        }

                        return LARGE_PROVIDER_BACKGROUND;
                    },
                    borderRadius: 15,
                    color: function () {
                        //function (context) {
                        // return context.dataset.backgroundColor;
                        return WHITE_GREY;
                        // return "red";
                    },
                    font: function (context) {
                        // const w = context.chart.width;
                        return {
                            // size: w < 512 ? 10 : 11,
                            size: 11,
                            weight: "300"
                        };
                    },
                    formatter: function (value, context) {
                        if (!hasDashLine || context?.datasetIndex !== 0) {
                            return "";
                        }

                        // return ["Large", "water provider"];
                        const w = context.chart.width;
                        const data = context?.dataset?.data ?? [];
                        let dataRenderIndex = w < 512 ? data?.length - 1 : data?.length - 1;
                        //data?.length - 2 : data?.length - 1;
                        dataRenderIndex = Math.max(dataRenderIndex, 0);

                        if (dataRenderIndex < 0 || context?.dataIndex !== dataRenderIndex) return "";

                        const chunkSize = w < 512 ? 4 : 4;
                        const wordsArray = _.chunk(_.split(dashAnnotation, " "), chunkSize);
                        return wordsArray.map(words => _.join(words, " "));
                    }
                }
            }
        },
        plugins: [ChartDataLabels]
    };

    return <ChartJsContainer chart={chart} className={styles.lineChart} />;
};
